import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  makeStyles, tokens, Title1, Title3, Spinner, Text, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  TabList, Tab, type SelectTabData, type SelectTabEvent,
  Dropdown, Option, Field, Input, Button,
  Menu, MenuTrigger, MenuList, MenuItem, MenuPopover, Tooltip,
} from '@fluentui/react-components';
import { apiGet, apiPost } from '../api/client';
import type { EvaluationListItem, EvaluationEngineDescriptor, SignalCareRole } from '../api/types';
import {
  listPromptEvals, listWinnersByRole,
  type PromptEvalRow, type PromptWinnerByRoleItem,
} from '../api/evaluations';
import {
  getGoldenSet, runCalibration, runVariance,
  type GoldenLabel, type CalibrationReport, type EngineAgreement,
  type DimAgreement, type VarianceReport, type DimVariance,
} from '../api/calibration';
import { useChatModels } from '../hooks/useChatModels';
import { providerBadgeColor } from '../lib/chatModelId';

// ============================================================================
// Phase 4 M5 — Evaluations page
//
// Two tabs:
//   • Prompt Evals — filtered flat rows from /api/evaluations/prompt,
//     with a "Best Model per Prompt" panel above the table (requires role).
//   • Search Evals — the legacy flat evaluations table + engine re-scoring
//     dropdown, preserved so we don't lose the M4-era functionality.
//
// Filter state lives in URL search params: role, models, from, to, scoreMin,
// scoreMax, q, tab. Deep-linkable. Filter changes update the URL directly;
// the URL is the source of truth.
// ============================================================================

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

const ALL_ROLES: SignalCareRole[] = [
  'MemberIntake', 'CareCoordinator', 'Lpn', 'Rn', 'Doctor', 'Billing', 'Claims',
];

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
  },
  filterBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    alignItems: 'end',
  },
  wrap: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px',
  },
  q: { maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  enginesBanner: {
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  engineList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '8px',
  },
  engineChip: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    padding: '8px 12px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    maxWidth: '320px',
  },
  winnerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
    marginTop: '12px',
  },
  winnerCard: {
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  rerunNotice: { color: tokens.colorPaletteGreenForeground1, fontSize: '12px', marginTop: '6px' },
  rerunError:  { color: tokens.colorPaletteRedForeground1, fontSize: '12px', marginTop: '6px' },
  explainer: {
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
    lineHeight: '18px',
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    paddingLeft: '10px',
    margin: '8px 0 4px',
  },
  mutedRow: { opacity: 0.55 },
  bestRow: { background: tokens.colorNeutralBackground2 },
  controlRow: { display: 'flex', gap: '12px', alignItems: 'end', flexWrap: 'wrap', marginTop: '12px' },
  varianceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '10px',
    marginTop: '12px',
  },
  varianceCard: {
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  loadingLine: { color: tokens.colorNeutralForeground2, fontSize: '13px', marginTop: '12px' },
});

// Agreement rate → Badge color. Higher agreement is better.
function agreementColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.75) return 'success';
  if (v >= 0.5) return 'warning';
  return 'danger';
}

function pct(v: number): string {
  return `${(v * 100).toFixed(0)}%`;
}

function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.7) return 'success';
  if (v >= 0.4) return 'warning';
  return 'danger';
}

// ---------------------------------------------------------------------------
// Filter param helpers — the URLSearchParams is the source of truth. This
// hook returns the current filter shape and a setter that patches the URL.
// ---------------------------------------------------------------------------

interface Filters {
  role?: SignalCareRole;
  chatModelIds: string[];
  from?: string;
  to?: string;
  scoreMin?: number;
  scoreMax?: number;
  q?: string;
}

function useFilters(): [Filters, (patch: Partial<Filters>) => void] {
  const [sp, setSp] = useSearchParams();
  const filters: Filters = useMemo(() => ({
    role: (sp.get('role') as SignalCareRole) || undefined,
    chatModelIds: (sp.get('models') ?? '').split(',').filter(Boolean),
    from: sp.get('from') || undefined,
    to: sp.get('to') || undefined,
    scoreMin: sp.get('scoreMin') ? Number(sp.get('scoreMin')) : undefined,
    scoreMax: sp.get('scoreMax') ? Number(sp.get('scoreMax')) : undefined,
    q: sp.get('q') || undefined,
  }), [sp]);

  function patch(p: Partial<Filters>) {
    const next = new URLSearchParams(sp);
    const merged = { ...filters, ...p };
    if (merged.role) next.set('role', merged.role); else next.delete('role');
    if (merged.chatModelIds.length) next.set('models', merged.chatModelIds.join(','));
    else next.delete('models');
    if (merged.from) next.set('from', merged.from); else next.delete('from');
    if (merged.to) next.set('to', merged.to); else next.delete('to');
    if (merged.scoreMin !== undefined) next.set('scoreMin', String(merged.scoreMin));
    else next.delete('scoreMin');
    if (merged.scoreMax !== undefined) next.set('scoreMax', String(merged.scoreMax));
    else next.delete('scoreMax');
    if (merged.q) next.set('q', merged.q); else next.delete('q');
    setSp(next, { replace: true });
  }

  return [filters, patch];
}

// ---------------------------------------------------------------------------
// Response shape from POST /api/evaluations/run — mirrors EvaluationDetail
// (Search-evals rerun path — unchanged from pre-M5).
// ---------------------------------------------------------------------------
interface RerunResult {
  evaluationId: string;
  chatRunId: string;
  groundedness: number;
  relevance: number;
  citation: number;
  safety: number;
  aggregate: number;
}

// ============================================================================
// Top-level page
// ============================================================================

export default function Evaluations() {
  const s = useStyles();
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get('tab');
  const tab = rawTab === 'search' ? 'search' : rawTab === 'calibration' ? 'calibration' : 'prompt';
  const [filters, patchFilters] = useFilters();
  const { models, byProvider } = useChatModels(true);

  return (
    <div className={s.page}>
      <div>
        <Title1 as="h1">Evaluations</Title1>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2 }}>
          Two lenses on evaluation history: Prompt Evals (matrix-benchmark cells,
          role-filterable, model-filterable) and Search Evals (single-question chat runs).
        </Text>
      </div>

      <TabList
        selectedValue={tab}
        onTabSelect={(_e: SelectTabEvent, d: SelectTabData) => {
          const next = new URLSearchParams(sp);
          if (d.value === 'prompt') next.delete('tab'); else next.set('tab', String(d.value));
          setSp(next, { replace: true });
        }}
      >
        <Tab value="prompt">Prompt Evals</Tab>
        <Tab value="search">Search Evals</Tab>
        <Tab value="calibration">Calibration</Tab>
      </TabList>

      {/* ---- Shared filter bar (Prompt/Search only — calibration has its own controls) ---- */}
      {tab !== 'calibration' && (
      <div className={s.section}>
        <Title3 as="h2" style={{ margin: 0, marginBottom: 8 }}>Filters</Title3>
        <div className={s.filterBar}>
          <Field label="Role">
            <Dropdown
              placeholder="Any role"
              value={filters.role ? ROLE_LABEL[filters.role] : ''}
              selectedOptions={filters.role ? [filters.role] : []}
              onOptionSelect={(_e, d) => patchFilters({ role: (d.optionValue as SignalCareRole) || undefined })}
            >
              <Option value="" text="Any role">Any role</Option>
              {ALL_ROLES.map(r => (
                <Option key={r} value={r} text={ROLE_LABEL[r]}>{ROLE_LABEL[r]}</Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Models">
            <Dropdown
              multiselect
              placeholder="Any model"
              value={
                filters.chatModelIds.length === 0
                  ? ''
                  : filters.chatModelIds.length === 1
                    ? filters.chatModelIds[0]
                    : `${filters.chatModelIds.length} selected`
              }
              selectedOptions={filters.chatModelIds}
              onOptionSelect={(_e, d) => patchFilters({ chatModelIds: (d.selectedOptions ?? []) as string[] })}
            >
              {Array.from(byProvider.entries()).map(([providerId, group]) => (
                <span key={providerId}>
                  {group.map(m => (
                    <Option key={m.chatModelId} value={m.chatModelId} text={m.displayLabel}>
                      {m.providerName} · {m.modelName}
                    </Option>
                  ))}
                </span>
              ))}
              {models.length === 0 && <Option value="" disabled text="No configured models">No configured models</Option>}
            </Dropdown>
          </Field>

          <Field label="From (ISO)">
            <Input value={filters.from ?? ''} placeholder="YYYY-MM-DD"
              onChange={(_e, d) => patchFilters({ from: d.value || undefined })} />
          </Field>
          <Field label="To (ISO)">
            <Input value={filters.to ?? ''} placeholder="YYYY-MM-DD"
              onChange={(_e, d) => patchFilters({ to: d.value || undefined })} />
          </Field>

          <Field label="Score min">
            <Input value={filters.scoreMin?.toString() ?? ''} placeholder="0.0"
              onChange={(_e, d) => patchFilters({ scoreMin: d.value ? Number(d.value) : undefined })} />
          </Field>
          <Field label="Score max">
            <Input value={filters.scoreMax?.toString() ?? ''} placeholder="1.0"
              onChange={(_e, d) => patchFilters({ scoreMax: d.value ? Number(d.value) : undefined })} />
          </Field>

          <Field label="Text search">
            <Input value={filters.q ?? ''} placeholder="question / model / prompt"
              onChange={(_e, d) => patchFilters({ q: d.value || undefined })} />
          </Field>

          <div style={{ alignSelf: 'end' }}>
            <Button
              appearance="subtle"
              onClick={() => patchFilters({ role: undefined, chatModelIds: [], from: undefined, to: undefined, scoreMin: undefined, scoreMax: undefined, q: undefined })}
            >
              Clear all
            </Button>
          </div>
        </div>
      </div>
      )}

      {tab === 'prompt' && <PromptEvalsTab filters={filters} />}
      {tab === 'search' && <SearchEvalsTab />}
      {tab === 'calibration' && <CalibrationTab />}
    </div>
  );
}

// ============================================================================
// Prompt Evals tab — winners panel + filtered rows
// ============================================================================

function PromptEvalsTab({ filters }: { filters: Filters }) {
  const s = useStyles();
  const [rows, setRows] = useState<PromptEvalRow[] | null>(null);
  const [winners, setWinners] = useState<PromptWinnerByRoleItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setErr(null);
    listPromptEvals({
      role: filters.role,
      chatModelIds: filters.chatModelIds.length ? filters.chatModelIds : undefined,
      from: filters.from,
      to: filters.to,
      scoreMin: filters.scoreMin,
      scoreMax: filters.scoreMax,
      q: filters.q,
      limit: 200,
    })
      .then(res => { if (!cancelled) setRows(res); })
      .catch(e => { if (!cancelled) { setErr((e as Error).message); setRows([]); } });
    return () => { cancelled = true; };
  }, [filters]);

  useEffect(() => {
    if (!filters.role) { setWinners(null); return; }
    let cancelled = false;
    listWinnersByRole(filters.role, 30)
      .then(res => { if (!cancelled) setWinners(res); })
      .catch(() => { if (!cancelled) setWinners([]); });
    return () => { cancelled = true; };
  }, [filters.role]);

  return (
    <>
      {/* ---- Best Model per Prompt panel (requires role) ---- */}
      {filters.role && (
        <div className={s.section}>
          <Title3 as="h2" style={{ margin: 0 }}>
            Best Model per Prompt — {ROLE_LABEL[filters.role]} · 30d rolling
          </Title3>
          <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
            Per-prompt winner across all runs in the last 30 days. Requires ≥3 successful
            samples per prompt/model pair to declare a winner; otherwise shows insufficient data.
          </Text>
          {winners === null && <Spinner size="tiny" label="Loading winners…" style={{ marginTop: 12 }} />}
          {winners && winners.length === 0 && (
            <Text size={300} block style={{ color: tokens.colorNeutralForeground3, marginTop: 12 }}>
              No prompts in this role.
            </Text>
          )}
          {winners && winners.length > 0 && (
            <div className={s.winnerGrid}>
              {winners.map(w => (
                <div key={w.promptId} className={s.winnerCard}>
                  <Text weight="semibold">{w.promptName}</Text>
                  {w.status === 'ok' && w.winningChatModelId ? (
                    <>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Badge
                          color={providerBadgeColor(w.winningChatModelId.split(':')[0])}
                          appearance="tint"
                        >
                          {w.providerName} · {w.modelName}
                        </Badge>
                        {w.avgOverallScore !== null && (
                          <Badge color={scoreColor(w.avgOverallScore)}>
                            {(w.avgOverallScore * 100).toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {w.sampleCount} sample{w.sampleCount === 1 ? '' : 's'}
                      </Text>
                    </>
                  ) : w.status === 'insufficient_data' ? (
                    <Badge color="warning" appearance="tint">Insufficient data ({w.sampleCount} samples)</Badge>
                  ) : (
                    <Badge appearance="tint">No data yet</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Rows table ---- */}
      <div className={s.section}>
        <Title3 as="h2" style={{ margin: 0 }}>Prompt Evals</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Flat matrix-benchmark cells (question × prompt × model). Filtered above.
          Errors are shown in-line, not hidden — reliability matters.
        </Text>
        {rows === null && !err && <Spinner label="Loading…" style={{ marginTop: 12 }} />}
        {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
        {rows && (
          <div className={s.wrap} style={{ marginTop: 12 }}>
            <Table size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Prompt</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Model</TableHeaderCell>
                  <TableHeaderCell>Question</TableHeaderCell>
                  <TableHeaderCell>Overall</TableHeaderCell>
                  <TableHeaderCell>Grounded</TableHeaderCell>
                  <TableHeaderCell>Cost</TableHeaderCell>
                  <TableHeaderCell>Latency</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9}>
                    <Text size={200}>No matching rows.</Text>
                  </TableCell></TableRow>
                )}
                {rows.map(r => (
                  <TableRow key={r.matrixResultId}>
                    <TableCell>
                      <Text size={200}>{new Date(r.createdAt).toLocaleString()}</Text>
                    </TableCell>
                    <TableCell><Text weight="semibold" size={200}>{r.promptName}</Text></TableCell>
                    <TableCell>
                      <Badge appearance="tint" size="small">
                        {ROLE_LABEL[r.role as SignalCareRole] ?? r.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge color={providerBadgeColor(r.chatModelId.split(':')[0])} appearance="tint" size="small">
                        {r.providerName} · {r.modelName}
                      </Badge>
                    </TableCell>
                    <TableCell className={s.q} title={r.questionText}>{r.questionText}</TableCell>
                    <TableCell>
                      {r.isError ? (
                        <Badge color="danger" appearance="tint" size="small">error</Badge>
                      ) : (
                        <Badge color={scoreColor(r.overallScore)}>{r.overallScore.toFixed(2)}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.isError ? '—' : <Badge color={scoreColor(r.groundednessScore)}>{r.groundednessScore.toFixed(2)}</Badge>}
                    </TableCell>
                    <TableCell>${r.estimatedCostUsd.toFixed(4)}</TableCell>
                    <TableCell>{r.latencyMs.toFixed(0)} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// Search Evals tab — legacy flat table + engine re-scoring dropdown
// ============================================================================

function SearchEvalsTab() {
  const s = useStyles();
  const [items, setItems] = useState<EvaluationListItem[] | null>(null);
  const [engines, setEngines] = useState<EvaluationEngineDescriptor[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ chatRunId: string; text: string; kind: 'ok' | 'err' } | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [list, engs] = await Promise.all([
          apiGet<EvaluationListItem[]>('/api/evaluations?limit=200'),
          apiGet<EvaluationEngineDescriptor[]>('/api/evaluations/engines'),
        ]);
        if (mounted) { setItems(list); setEngines(engs); }
      } catch (e) { if (mounted) setErr((e as Error).message); }
    })();
    return () => { mounted = false; };
  }, []);

  async function rerun(chatRunId: string, engineName: string) {
    setBusyRow(chatRunId);
    setNotice(null);
    try {
      const result = await apiPost<RerunResult>('/api/evaluations/run', { chatRunId, engineName });
      setItems((prev) =>
        prev ? prev.map((it) => it.chatRunId === chatRunId
          ? { ...it, groundedness: result.groundedness, relevance: result.relevance,
              citation: result.citation, safety: result.safety, aggregate: result.aggregate }
          : it) : prev);
      setNotice({ chatRunId, text: `Re-scored with ${engineName}.`, kind: 'ok' });
    } catch (e) {
      setNotice({ chatRunId, text: (e as Error).message, kind: 'err' });
    } finally {
      setBusyRow(null);
    }
  }

  return (
    <div>
      {engines && engines.length > 0 && (
        <div className={s.enginesBanner}>
          <Text weight="semibold">Registered evaluation engines</Text>
          <div className={s.engineList}>
            {engines.map((e) => (
              <div key={e.name} className={s.engineChip}>
                <Text weight="semibold" size={200}>
                  {e.name} <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>v{e.version}</Text>
                </Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{e.description}</Text>
              </div>
            ))}
          </div>
        </div>
      )}
      {!items && !err && <Spinner label="Loading…" />}
      {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
      {items && (
        <div className={s.wrap}>
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Question</TableHeaderCell>
                <TableHeaderCell>Model</TableHeaderCell>
                <TableHeaderCell>Grounded</TableHeaderCell>
                <TableHeaderCell>Relevance</TableHeaderCell>
                <TableHeaderCell>Citation</TableHeaderCell>
                <TableHeaderCell>Safety</TableHeaderCell>
                <TableHeaderCell>Aggregate</TableHeaderCell>
                <TableHeaderCell>Cost</TableHeaderCell>
                <TableHeaderCell>Latency</TableHeaderCell>
                <TableHeaderCell>Re-score</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={10}><Text size={200}>No evaluations yet — run a chat query.</Text></TableCell></TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.evaluationId}>
                  <TableCell className={s.q} title={it.question}>{it.question}</TableCell>
                  <TableCell>{it.modelName}</TableCell>
                  <TableCell><Badge color={scoreColor(it.groundedness)}>{it.groundedness.toFixed(2)}</Badge></TableCell>
                  <TableCell><Badge color={scoreColor(it.relevance)}>{it.relevance.toFixed(2)}</Badge></TableCell>
                  <TableCell><Badge color={scoreColor(it.citation)}>{it.citation.toFixed(2)}</Badge></TableCell>
                  <TableCell><Badge color={scoreColor(it.safety)}>{it.safety.toFixed(2)}</Badge></TableCell>
                  <TableCell><Badge color={scoreColor(it.aggregate)}>{it.aggregate.toFixed(2)}</Badge></TableCell>
                  <TableCell>${it.costUsd.toFixed(4)}</TableCell>
                  <TableCell>{it.latencyMs.toFixed(0)} ms</TableCell>
                  <TableCell>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Tooltip content="Re-score this run with a different engine — no LLM call." relationship="label">
                          <Button size="small" appearance="subtle" disabled={busyRow === it.chatRunId || !engines}>
                            {busyRow === it.chatRunId ? 'Scoring…' : 'Engine ▾'}
                          </Button>
                        </Tooltip>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {(engines ?? []).map((e) => (
                            <MenuItem key={e.name} onClick={() => rerun(it.chatRunId, e.name)}>
                              {e.name} <Text size={100} style={{ color: tokens.colorNeutralForeground3, marginLeft: 6 }}>v{e.version}</Text>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                    {notice?.chatRunId === it.chatRunId && (
                      <div className={notice.kind === 'ok' ? s.rerunNotice : s.rerunError}>
                        {notice.text}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Calibration tab - engine agreement vs a hand-labeled golden set + variance
//
// Phase 8: "the eval you can trust". Runs every engine over the golden set and
// reports how well each agrees with human labels. The headline is per-dimension
// within-tolerance agreement (LLM-judge agrees X% within tolerance; keyword only
// Y%). Engines arrive best-agreement-first; ran=false engines render muted with
// their reason. A collapsible golden set is the transparency artifact, and the
// variance panel probes how stable a model-based engine's scores are on repeats.
// ============================================================================

const DIMS: { key: keyof Pick<EngineAgreement, 'groundedness' | 'relevance' | 'citation' | 'safety'>; label: string }[] = [
  { key: 'groundedness', label: 'Grounded' },
  { key: 'relevance', label: 'Relevance' },
  { key: 'citation', label: 'Citation' },
  { key: 'safety', label: 'Safety' },
];

const VDIMS: { key: keyof Pick<VarianceReport, 'groundedness' | 'relevance' | 'citation' | 'safety' | 'aggregate'>; label: string }[] = [
  { key: 'groundedness', label: 'Grounded' },
  { key: 'relevance', label: 'Relevance' },
  { key: 'citation', label: 'Citation' },
  { key: 'safety', label: 'Safety' },
  { key: 'aggregate', label: 'Aggregate' },
];

function CalibrationTab() {
  const s = useStyles();

  // Golden set - loaded once, shared by the transparency table + variance picker.
  const [golden, setGolden] = useState<GoldenLabel[] | null>(null);
  const [goldenErr, setGoldenErr] = useState<string | null>(null);
  const [showGolden, setShowGolden] = useState(false);

  // Calibration report.
  const [report, setReport] = useState<CalibrationReport | null>(null);
  const [running, setRunning] = useState(false);
  const [calErr, setCalErr] = useState<string | null>(null);

  // Variance panel.
  const [goldenId, setGoldenId] = useState<string>('');
  const [repeats, setRepeats] = useState<string>('5');
  const [variance, setVariance] = useState<VarianceReport | null>(null);
  const [varRunning, setVarRunning] = useState(false);
  const [varErr, setVarErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getGoldenSet()
      .then(res => {
        if (cancelled) return;
        setGolden(res);
        if (res.length > 0) setGoldenId(res[0].id);
      })
      .catch(e => { if (!cancelled) setGoldenErr((e as Error).message); });
    return () => { cancelled = true; };
  }, []);

  async function onRunCalibration() {
    setRunning(true);
    setCalErr(null);
    try {
      const res = await runCalibration();
      setReport(res);
    } catch (e) {
      setCalErr((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  async function onMeasureVariance() {
    if (!goldenId) return;
    setVarRunning(true);
    setVarErr(null);
    setVariance(null);
    try {
      const n = Number(repeats);
      const res = await runVariance({
        goldenId,
        repeats: Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined,
      });
      setVariance(res);
    } catch (e) {
      // 400 (no configured judge) arrives as "API 400: <plain message>" - show it calmly.
      setVarErr((e as Error).message);
    } finally {
      setVarRunning(false);
    }
  }

  const goldenCount = golden?.length ?? report?.goldenSetSize ?? 0;

  return (
    <>
      {/* ---- Engine agreement ---- */}
      <div className={s.section}>
        <Title3 as="h2" style={{ margin: 0 }}>Calibration - engine agreement with humans</Title3>
        <Text className={s.explainer} block>
          Why you can trust this: every scoring engine is run over a hand-labeled golden set,
          then compared against the human labels. The headline metric is the within-tolerance
          agreement rate per dimension - how often the engine lands within the tolerance band of
          the human score. Higher is better. Engines are ordered best-agreement-first.
        </Text>

        <div className={s.controlRow}>
          <Button appearance="primary" onClick={onRunCalibration} disabled={running}>
            {running ? 'Running...' : 'Run calibration'}
          </Button>
          {report && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              Golden set: {report.goldenSetSize} labeled example{report.goldenSetSize === 1 ? '' : 's'}
              {'  ·  '}Tolerance: +/-{report.tolerance.toFixed(2)}
              {'  ·  '}Generated {new Date(report.generatedAt).toLocaleString()}
            </Text>
          )}
        </div>

        {running && (
          <Text className={s.loadingLine} block>
            Running calibration over {goldenCount || 'the'} labeled example{goldenCount === 1 ? '' : 's'} - scoring with every engine and comparing to human labels...
          </Text>
        )}
        {calErr && <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: 12 }}>{calErr}</Text>}

        {report && !running && (
          <div className={s.wrap} style={{ marginTop: 12 }}>
            <Table size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Engine</TableHeaderCell>
                  {DIMS.map(d => (
                    <TableHeaderCell key={d.key}>{d.label} agree</TableHeaderCell>
                  ))}
                  <TableHeaderCell>Aggregate agree</TableHeaderCell>
                  <TableHeaderCell>Aggregate MAE</TableHeaderCell>
                  <TableHeaderCell>Aggregate Pearson</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.engines.length === 0 && (
                  <TableRow><TableCell colSpan={8}><Text size={200}>No engines reported.</Text></TableCell></TableRow>
                )}
                {report.engines.map(eng => {
                  const isBest = report.bestByAgreement === eng.engineName;
                  const agg: DimAgreement = eng.aggregate;
                  return (
                    <TableRow
                      key={eng.engineName}
                      className={!eng.ran ? s.mutedRow : isBest ? s.bestRow : undefined}
                    >
                      <TableCell>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Text weight="semibold" size={200}>{eng.engineName}</Text>
                            <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>v{eng.engineVersion}</Text>
                            {eng.isModelBased && <Badge appearance="tint" size="small">model</Badge>}
                            {isBest && eng.ran && <Badge color="success" appearance="filled" size="small">best agreement</Badge>}
                          </div>
                          {!eng.ran && (
                            <Text size={100} style={{ color: tokens.colorPaletteRedForeground1 }}>
                              {eng.error ? `${eng.engineName}: ${eng.error}` : 'unavailable'}
                            </Text>
                          )}
                        </div>
                      </TableCell>
                      {DIMS.map(d => {
                        const dim = eng[d.key] as DimAgreement;
                        return (
                          <TableCell key={d.key}>
                            {eng.ran
                              ? <Badge color={agreementColor(dim.withinToleranceRate)}>{pct(dim.withinToleranceRate)}</Badge>
                              : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        {eng.ran
                          ? <Badge color={agreementColor(agg.withinToleranceRate)}>{pct(agg.withinToleranceRate)}</Badge>
                          : '-'}
                      </TableCell>
                      <TableCell>{eng.ran ? agg.meanAbsoluteError.toFixed(3) : '-'}</TableCell>
                      <TableCell>
                        {eng.ran
                          ? (agg.pearsonCorrelation === null ? 'n/a' : agg.pearsonCorrelation.toFixed(2))
                          : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* ---- Golden set transparency artifact ---- */}
      <div className={s.section}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title3 as="h2" style={{ margin: 0 }}>Golden set</Title3>
          <Button appearance="subtle" size="small" onClick={() => setShowGolden(v => !v)}>
            {showGolden ? 'Hide' : 'Show'} the labeled examples
          </Button>
        </div>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          The hand-labeled examples the engines are measured against. This is the transparency
          artifact - the human scores here are the ground truth every agreement number is computed from.
        </Text>
        {goldenErr && <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: 8 }}>{goldenErr}</Text>}
        {showGolden && golden === null && !goldenErr && <Spinner size="tiny" label="Loading golden set..." style={{ marginTop: 12 }} />}
        {showGolden && golden && (
          <div className={s.wrap} style={{ marginTop: 12 }}>
            <Table size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>ID</TableHeaderCell>
                  <TableHeaderCell>Question</TableHeaderCell>
                  <TableHeaderCell>Answer</TableHeaderCell>
                  <TableHeaderCell>Grounded</TableHeaderCell>
                  <TableHeaderCell>Relevance</TableHeaderCell>
                  <TableHeaderCell>Citation</TableHeaderCell>
                  <TableHeaderCell>Safety</TableHeaderCell>
                  <TableHeaderCell>Aggregate</TableHeaderCell>
                  <TableHeaderCell>Note</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {golden.length === 0 && (
                  <TableRow><TableCell colSpan={9}><Text size={200}>Golden set is empty.</Text></TableCell></TableRow>
                )}
                {golden.map(g => (
                  <TableRow key={g.id}>
                    <TableCell><Text weight="semibold" size={200}>{g.id}</Text></TableCell>
                    <TableCell className={s.q} title={g.question}>{g.question}</TableCell>
                    <TableCell className={s.q} title={g.answer}>{g.answer}</TableCell>
                    <TableCell><Badge color={scoreColor(g.humanGroundedness)}>{g.humanGroundedness.toFixed(2)}</Badge></TableCell>
                    <TableCell><Badge color={scoreColor(g.humanRelevance)}>{g.humanRelevance.toFixed(2)}</Badge></TableCell>
                    <TableCell><Badge color={scoreColor(g.humanCitation)}>{g.humanCitation.toFixed(2)}</Badge></TableCell>
                    <TableCell><Badge color={scoreColor(g.humanSafety)}>{g.humanSafety.toFixed(2)}</Badge></TableCell>
                    <TableCell><Badge color={scoreColor(g.humanAggregate)}>{g.humanAggregate.toFixed(2)}</Badge></TableCell>
                    <TableCell className={s.q} title={g.note}>{g.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* ---- Score variance mini-panel ---- */}
      <div className={s.section}>
        <Title3 as="h2" style={{ margin: 0 }}>Score variance</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Model-based judges are not deterministic. Score the same golden example several times and
          measure the spread - a trustworthy judge is stable across repeats. Reported as mean +/- 95% CI.
        </Text>

        <div className={s.controlRow}>
          <Field label="Golden example">
            <Dropdown
              placeholder="Pick an example"
              value={goldenId}
              selectedOptions={goldenId ? [goldenId] : []}
              onOptionSelect={(_e, d) => setGoldenId((d.optionValue as string) || '')}
              disabled={!golden || golden.length === 0}
            >
              {(golden ?? []).map(g => (
                <Option key={g.id} value={g.id} text={`${g.id} - ${g.question}`}>
                  {g.id} - {g.question}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="Repeats">
            <Input
              type="number"
              value={repeats}
              onChange={(_e, d) => setRepeats(d.value)}
              style={{ maxWidth: 100 }}
            />
          </Field>
          <div style={{ alignSelf: 'end' }}>
            <Button appearance="primary" onClick={onMeasureVariance} disabled={varRunning || !goldenId}>
              {varRunning ? 'Measuring...' : 'Measure variance'}
            </Button>
          </div>
        </div>

        {varRunning && (
          <Text className={s.loadingLine} block>
            Scoring {goldenId || 'the example'} {repeats}x with the judge - computing mean and spread...
          </Text>
        )}
        {varErr && (
          <Text style={{ color: tokens.colorNeutralForeground2, display: 'block', marginTop: 12 }}>
            {varErr}
          </Text>
        )}

        {variance && !varRunning && (
          <>
            <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 12 }}>
              {variance.engineName} <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>v{variance.engineVersion}</Text>
              {'  ·  '}{variance.repeats} repeat{variance.repeats === 1 ? '' : 's'} of {variance.inputLabel}
            </Text>
            <div className={s.varianceGrid}>
              {VDIMS.map(d => {
                const v = variance[d.key] as DimVariance;
                return (
                  <div key={d.key} className={s.varianceCard}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{d.label}</Text>
                    <Text weight="semibold">{v.mean.toFixed(2)} +/- {v.ci95Margin.toFixed(2)}</Text>
                    <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                      sd {v.stdDev.toFixed(3)} - n {v.samples.length}
                    </Text>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
